import { motion } from "framer-motion";

/**
 * MoneyStrip — linha compacta horizontal pra um movimento financeiro
 * (ou um saldo do dia). Acabamento premium: barra colorida 6×24 à
 * esquerda + label tracking-wide + descrição + valor à direita.
 *
 * Props:
 *   label      — micro-text (ex: "ALMOÇO", "SALÁRIO")
 *   description?— linha secundária (ex: "iFood — Carolina Garcia")
 *   amount     — número (já em valor absoluto)
 *   type       — 'expense' | 'income' | 'neutral'
 *   accent?    — classe tailwind ("bg-category-food") OU hex pra a barra
 *                lateral. Se omitido, deriva do type.
 *   meta?      — texto à direita do valor (data curta etc.)
 */
const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const TYPE_AMOUNT = {
  expense: "text-semantic-danger",
  income:  "text-semantic-success",
  neutral: "text-slate-300",
};
const TYPE_PREFIX = { expense: "−", income: "+", neutral: "" };
const TYPE_DEFAULT_ACCENT = {
  expense: "bg-semantic-danger/60",
  income:  "bg-semantic-success/60",
  neutral: "bg-slate-500/60",
};

export default function MoneyStrip({
  label,
  description,
  amount,
  type = "expense",
  accent,
  meta,
}) {
  const amountClass = TYPE_AMOUNT[type] || TYPE_AMOUNT.neutral;
  const prefix = TYPE_PREFIX[type] || "";
  const isHex = typeof accent === "string" && accent.startsWith("#");
  const accentStyle = isHex ? { backgroundColor: accent } : {};
  const accentClass = isHex ? "" : (accent || TYPE_DEFAULT_ACCENT[type]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -2 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
      className="flex items-stretch gap-3 py-1.5"
      data-testid="ds-money-strip"
    >
      <span className={`w-1.5 rounded-full ${accentClass}`} style={accentStyle} aria-hidden />
      <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
        <div className="min-w-0">
          {label ? (
            <p className="text-[10px] tracking-[0.1em] uppercase text-slate-500 font-medium leading-tight">
              {label}
            </p>
          ) : null}
          {description ? (
            <p className="text-sm text-slate-200 truncate">{description}</p>
          ) : null}
        </div>
        <div className="flex flex-col items-end shrink-0">
          <p className={`text-sm font-semibold tabular-nums ${amountClass}`}>
            {prefix}
            {fmtBRL(Math.abs(amount || 0))}
          </p>
          {meta ? <p className="text-[10px] text-slate-500 tabular-nums">{meta}</p> : null}
        </div>
      </div>
    </motion.div>
  );
}
